"use client";

import { useUser } from "@/components/clients/LocaleToStorage";
import {
  DatasheetDownloadItem,
  fetchLatestDatasheetDownload,
  resolveDatasheetDownload,
} from "@/lib/product-datasheet";
import { useScopedI18n } from "@/messages/client";
import { usePathname } from "next/navigation";
import { useMemo, useState } from "react";
import useSWR from "swr";

type DatasheetPropItem =
  | string
  | null
  | undefined
  | DatasheetDownloadItem<string>;

function normalizeDatasheetItem(
  item: DatasheetPropItem
): DatasheetDownloadItem<string> {
  if (item && typeof item === "object" && "file" in item) {
    return item;
  }

  return { file: item };
}

function openDatasheetFile(file: string, filename?: string) {
  const href = filename
    ? `${process.env.NEXT_PUBLIC_DIRECTUS_ASSETS}/${file}/${filename}`
    : `${process.env.NEXT_PUBLIC_DIRECTUS_ASSETS}/${file}`;
  const link = document.createElement("a");

  link.href = href;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.download = "";
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export default function Datasheet(props: {
  datasheet?: DatasheetPropItem[];
  datasheetTexts?: (string | null | undefined)[];
  name: string;
  productId?: string | number | null;
}) {
  const t = useScopedI18n("产品");

  const pathname = usePathname();

  const { user } = useUser();
  const [isCheckingDownload, setIsCheckingDownload] = useState(false);

  const datasheets = useMemo(
    () => props.datasheet?.map(normalizeDatasheetItem) ?? [],
    [props.datasheet]
  );

  const hasDownloadConfig = useMemo(
    () =>
      props.datasheet?.some(
        (item) => item && typeof item === "object" && "file" in item
      ),
    [props.datasheet]
  );

  const currentDownload = useMemo(() => {
    if (hasDownloadConfig) {
      return resolveDatasheetDownload(datasheets, user?.role?.level);
    }

    const level = user?.role?.level;
    const file = level ? datasheets[level - 1]?.file || null : null;

    return {
      file,
      canDownload: Boolean(file),
      requiresLogin: false,
    };
  }, [datasheets, hasDownloadConfig, user?.role?.level]);

  const currentFile = currentDownload.file;

  const currentFileName = useMemo(() => {
    const level = user?.role?.level;
    if (level) {
      return props.datasheetTexts?.[level - 1];
    }
  }, [props.datasheetTexts, user?.role?.level]);

  const { data } = useSWR<{
    data: {
      id: string;
      filename_download: string;
      title: string;
      type: string;
      uploaded_on: string;
    };
  }>(
    () =>
      currentFile
        ? `${process.env.NEXT_PUBLIC_API_HOST}/files/${currentFile}`
        : null,
    async (url: string) => {
      return fetch(url).then((res) => res.json());
    }
  );

  if (!data?.data) {
    return null;
  }

  const loginHref = `/login?redirect_url=${encodeURIComponent(pathname)}`;

  const handleDownloadClick = async () => {
    if (isCheckingDownload) {
      return;
    }

    setIsCheckingDownload(true);

    try {
      const latestDownload =
        hasDownloadConfig && props.productId
          ? await fetchLatestDatasheetDownload({
              productId: props.productId,
              pathname,
              userLevel: user?.role?.level,
            })
          : null;
      const download = latestDownload ?? currentDownload;

      if (!latestDownload && props.productId && !user) {
        window.location.href = loginHref;
        return;
      }

      if (!download.canDownload || !download.file) {
        window.location.href = loginHref;
        return;
      }

      openDatasheetFile(
        download.file,
        download.file === data.data?.id
          ? data.data?.filename_download
          : undefined
      );
    } finally {
      setIsCheckingDownload(false);
    }
  };

  return (
    <button
      type="button"
      disabled={isCheckingDownload}
      onClick={handleDownloadClick}
      className="flex shrink-0 appearance-none items-center space-x-2 border-0 bg-transparent p-0 disabled:cursor-wait"
    >
      <img className="h-5 w-5" src="/icons/pdf.svg" alt="DataSheet File" />

      <div className="text-sm text-[#CC1623] lg:text-base">
        {currentFileName
          ? t<any, any>("下载{name}", { name: currentFileName })
          : t("下载数据表")}
      </div>
    </button>
  );
}
